// Module-level bridge between the general-agent chat panel and the
// notification layer. The chat surface records which conversation is on
// screen (so approval toasts for it can be suppressed) and registers an
// opener so a toast click can bring that conversation into view in-tab.
//
// Run-native chats select by run id, while notifications identify the chat by
// agent-conversation id, so the opener carries both: the caller picks whichever
// id its surface selects with.

import { type AgentChatIds } from '@apps/web/lib/notification_navigation';

export type AgentConversationOpenIds = AgentChatIds & {
    readonly organizationId: string | null;
    readonly runMode?: 'live' | 'sandbox';
};

type ActiveAgentConversation = {
    readonly organizationId: string | null;
    readonly id: string;
};

let activeAgentConversation: ActiveAgentConversation | null = null;
let openHandler: ((ids: AgentConversationOpenIds) => void) | null = null;

export function setActiveAgentConversation(
    id: string | null,
    organizationId: string | null = null,
): void {
    activeAgentConversation =
        id === null
            ? null
            : {
                  id,
                  organizationId,
              };
}

export function getActiveAgentConversation(
    organizationId?: string | null,
): string | null {
    if (activeAgentConversation === null) {
        return null;
    }
    if (
        organizationId !== undefined &&
        activeAgentConversation.organizationId !== organizationId
    ) {
        return null;
    }
    return activeAgentConversation.id;
}

export function registerAgentConversationOpener(
    handler: (ids: AgentConversationOpenIds) => void,
): () => void {
    openHandler = handler;
    return () => {
        if (openHandler === handler) {
            openHandler = null;
        }
    };
}

export function openAgentConversation(ids: AgentConversationOpenIds): boolean {
    if (openHandler === null) {
        return false;
    }
    openHandler(ids);
    return true;
}
